(() => {
  if (customElements.get('healthy-video-stories')) return;
  const sections = new Set();
  let active = null;
  let scrollFrame = null;
  const entries = () => Array.from(sections).flatMap((section) => section.entries);
  const preferencesAllow = (entry) => entry.owner.autoplay && !entry.owner.reduced.matches && !navigator.connection?.saveData;
  const policyAllows = (entry) => !document.hidden && preferencesAllow(entry) && entry.ratio >= .12;
  const clearTimer = (entry) => {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = null;
  };
  const sync = (entry) => {
    entry.card.classList.toggle('is-playing', !entry.video.paused);
    entry.card.classList.toggle('is-loading', entry.pending);
    if (!preferencesAllow(entry)) entry.video.classList.remove('is-ready');
  };
  const stop = (entry) => {
    if (!entry) return;
    if (active === entry) active = null;
    entry.request += 1;
    entry.pending = false;
    entry.playing = false;
    clearTimer(entry);
    entry.video.autoplay = false;
    entry.video.removeAttribute('autoplay');
    entry.video.pause();
    sync(entry);
  };
  const pauseActive = () => stop(active);
  const positionStart = (entry) => {
    if (entry.positioned) return true;
    if (entry.video.readyState < 1 || entry.video.seeking || entry.positioning) return false;
    const duration = entry.video.duration;
    const target = Number.isFinite(duration) && duration > 0 ? Math.min(entry.startTime, Math.max(0, duration - .1)) : entry.startTime;
    if (Math.abs(entry.video.currentTime - target) < .05) {
      entry.positioned = true;
      return true;
    }
    try {
      entry.positioning = true;
      entry.video.currentTime = target;
      if (!entry.video.seeking && Math.abs(entry.video.currentTime - target) < .05) {
        entry.positioning = false;
        entry.positioned = true;
      }
    } catch (_) {
      entry.positioning = false;
    }
    return entry.positioned;
  };
  const fail = (entry) => {
    const desktop = entry.video.dataset.videoUrl;
    const mobile = entry.video.dataset.videoMobileUrl;
    const fallback = !entry.fallbackUsed && mobile && desktop && mobile !== desktop && entry.source === mobile;
    stop(entry);
    entry.video.classList.remove('is-ready');
    entry.prepared = false;
    if (fallback) {
      entry.fallbackUsed = true;
      entry.owner.prepare(entry);
      choose();
      return;
    }
    entry.failed = true;
    if (entry.error) entry.error.hidden = false;
    choose();
  };
  const rejected = (entry, error, request) => {
    if (entry.request !== request || active !== entry) return;
    clearTimer(entry);
    entry.pending = false;
    if (error?.name === 'NotSupportedError') {
      fail(entry);
      return;
    }
    active = null;
    entry.playing = false;
    entry.video.autoplay = false;
    entry.video.removeAttribute('autoplay');
    if (error?.name !== 'AbortError') entry.autoplayBlocked = true;
    sync(entry);
  };
  const start = (entry) => {
    if (!policyAllows(entry) || !entry.owner.prepare(entry) || !positionStart(entry)) return;
    if (active !== entry) pauseActive();
    active = entry;
    const request = ++entry.request;
    entry.pending = true;
    entry.lastAttempt = Date.now();
    if (entry.error) entry.error.hidden = true;
    entry.video.muted = true;
    entry.video.defaultMuted = true;
    entry.video.playsInline = true;
    entry.video.autoplay = true;
    entry.video.setAttribute('autoplay', '');
    try {
      const playback = entry.video.play();
      sync(entry);
      clearTimer(entry);
      entry.timer = setTimeout(() => {
        entry.timer = null;
        if (entry.request !== request || active !== entry || !entry.pending) return;
        entry.pending = false;
        if (entry.video.paused) {
          active = null;
          entry.autoplayBlocked = true;
          entry.video.autoplay = false;
          entry.video.removeAttribute('autoplay');
        }
        sync(entry);
      }, 8000);
      Promise.resolve(playback).then(() => {
        if (entry.request !== request || active !== entry) return;
        clearTimer(entry);
        entry.pending = false;
        sync(entry);
      }).catch((error) => rejected(entry, error, request));
    } catch (error) {
      rejected(entry, error, request);
    }
  };
  const choose = () => {
    const eligible = entries().filter((entry) => !entry.failed && !entry.autoplayBlocked && policyAllows(entry));
    const best = eligible.sort((a, b) => b.ratio - a.ratio)[0];
    if (!best) { pauseActive(); return; }
    if (active === best && (best.pending || !best.video.paused)) return;
    start(best);
  };
  const refresh = (retry = false) => {
    const height = window.innerHeight || document.documentElement.clientHeight;
    const width = window.innerWidth || document.documentElement.clientWidth;
    entries().forEach((entry) => {
      const rect = entry.media.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
      const visibleWidth = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
      entry.ratio = rect.width && rect.height ? visibleHeight * visibleWidth / (rect.width * rect.height) : 0;
      const near = rect.bottom >= -400 && rect.top <= height + 400 && rect.right > 0 && rect.left < width;
      if (near && !entry.failed) entry.owner.prepare(entry);
      if (retry && entry.autoplayBlocked && policyAllows(entry)) entry.autoplayBlocked = false;
    });
    choose();
  };
  const retryAfterGesture = (event) => {
    if (event.isTrusted === false || event.repeat || document.hidden) return;
    refresh(true);
  };
  const onScroll = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;
      const now = Date.now();
      entries().forEach((entry) => {
        if (entry.autoplayBlocked && now - entry.lastAttempt > 1000) entry.autoplayBlocked = false;
      });
      refresh();
    });
  };
  class HealthyVideoStories extends HTMLElement {
    connectedCallback() {
      if (this.entries) return;
      this.autoplay = this.dataset.autoplay === 'true';
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.entries = Array.from(this.querySelectorAll('[data-hl-video-story]')).map((card) => {
        const video = card.querySelector('[data-hl-video]');
        const media = card.querySelector('[data-hl-video-media]');
        if (!video || !media) return null;
        const startTime = Math.max(0, Number(video.dataset.startTime) || 0);
        const entry = {owner: this, card, video, media, ratio: 0, prepared: false,
          autoplayBlocked: false, failed: false, fallbackUsed: false, startTime,
          positioned: startTime === 0, positioning: false, request: 0,
          pending: false, playing: false, lastAttempt: 0, listeners: []};
        const listen = (type, callback) => {
          video.addEventListener(type, callback);
          entry.listeners.push({type, callback});
        };
        entry.error = card.querySelector('[data-hl-video-error]');
        const poster = card.querySelector('[data-hl-video-poster]');
        if (!video.poster && poster) video.poster = poster.currentSrc || poster.src;
        video.hidden = false;
        video.controls = false;
        video.removeAttribute('controls');
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.muted = true;
        video.defaultMuted = true;
        video.loop = startTime === 0;
        if (video.loop) video.setAttribute('loop', '');
        else video.removeAttribute('loop');
        video.playsInline = true;
        video.disablePictureInPicture = true;
        video.disableRemotePlayback = true;
        video.style.pointerEvents = 'none';
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('tabindex', '-1');
        if (!video.paused) video.pause();
        listen('playing', () => {
          if (active !== entry || !policyAllows(entry) || !entry.positioned) { stop(entry); return; }
          clearTimer(entry);
          entry.pending = false;
          entry.playing = true;
          video.classList.add('is-ready');
          sync(entry);
        });
        listen('pause', () => {
          if (!video.paused) return;
          if (entry.pending && !entry.playing) { sync(entry); return; }
          if (active === entry) {
            active = null;
            entry.request += 1;
            entry.pending = false;
            clearTimer(entry);
            video.autoplay = false;
            video.removeAttribute('autoplay');
          }
          entry.playing = false;
          sync(entry);
        });
        const ready = () => {
          if (!entry.prepared || entry.failed) return;
          positionStart(entry);
          if (!entry.pending) choose();
        };
        listen('loadedmetadata', ready);
        listen('loadeddata', ready);
        listen('canplay', ready);
        listen('seeked', () => {
          entry.positioning = false;
          ready();
        });
        listen('ended', () => {
          stop(entry);
          if (entry.startTime > 0) entry.positioned = false;
          positionStart(entry);
          choose();
        });
        listen('error', () => { if (video.error) fail(entry); });
        sync(entry);
        return entry;
      }).filter(Boolean);
      sections.add(this);
      this.preferenceChange = () => { this.entries.forEach(sync); refresh(); };
      if (this.reduced.addEventListener) this.reduced.addEventListener('change', this.preferenceChange);
      else this.reduced.addListener(this.preferenceChange);
      navigator.connection?.addEventListener?.('change', this.preferenceChange);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((observed) => {
          observed.forEach((change) => {
            const entry = this.entries.find((item) => item.media === change.target);
            if (!entry) return;
            entry.ratio = change.isIntersecting ? change.intersectionRatio : 0;
            if (!change.isIntersecting) stop(entry);
          });
          choose();
        }, {threshold: [0,.12,.25,.5,.75,1]});
        this.preloader = new IntersectionObserver((observed) => {
          observed.forEach((change) => {
            if (!change.isIntersecting) return;
            const entry = this.entries.find((item) => item.media === change.target);
            if (entry && !entry.failed) this.prepare(entry);
          });
        }, {rootMargin: '400px 0px', threshold: 0});
        this.entries.forEach((entry) => {
          this.observer.observe(entry.media);
          this.preloader.observe(entry.media);
        });
      }
      refresh();
    }
    prepare(entry) {
      if (entry.prepared) return true;
      if (!preferencesAllow(entry) || document.hidden) return false;
      entry.source = !entry.fallbackUsed && matchMedia('(max-width: 749px)').matches && entry.video.dataset.videoMobileUrl
        ? entry.video.dataset.videoMobileUrl : entry.video.dataset.videoUrl;
      if (!entry.source) return false;
      const source = entry.startTime > 0 ? `${entry.source.split('#')[0]}#t=${entry.startTime}` : entry.source;
      entry.prepared = true;
      entry.positioned = entry.startTime === 0;
      entry.positioning = false;
      entry.video.preload = 'metadata';
      entry.video.setAttribute('preload', 'metadata');
      const changed = entry.video.getAttribute('src') !== source;
      if (changed) entry.video.src = source;
      if (changed || entry.video.readyState === 0 || entry.video.error) entry.video.load();
      if (entry.video.readyState >= 1) positionStart(entry);
      return true;
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.preloader?.disconnect();
      if (this.reduced?.removeEventListener) this.reduced.removeEventListener('change', this.preferenceChange);
      else this.reduced?.removeListener(this.preferenceChange);
      navigator.connection?.removeEventListener?.('change', this.preferenceChange);
      sections.delete(this);
      this.entries?.forEach((entry) => {
        stop(entry);
        entry.listeners.forEach(({type, callback}) => entry.video.removeEventListener(type, callback));
      });
      this.entries = null;
      choose();
    }
  }
  customElements.define('healthy-video-stories', HealthyVideoStories);
  document.addEventListener('visibilitychange', () => refresh());
  document.addEventListener('touchstart', retryAfterGesture, {capture: true, passive: true});
  document.addEventListener('touchend', retryAfterGesture, {capture: true, passive: true});
  document.addEventListener('click', retryAfterGesture, true);
  document.addEventListener('keydown', retryAfterGesture, true);
  window.addEventListener('scroll', onScroll, {passive: true});
  window.addEventListener('resize', onScroll);
  window.addEventListener('pagehide', pauseActive);
  window.addEventListener('pageshow', () => refresh());
  window.addEventListener('focus', () => refresh());
})();

(() => {
  if (customElements.get('healthy-story-products')) return;
  class HealthyStoryProducts extends HTMLElement {
    connectedCallback() {
      if (this.track) return;
      this.track = this.querySelector('[data-hl-product-track]');
      this.previous = this.querySelector('[data-hl-product-prev]');
      this.next = this.querySelector('[data-hl-product-next]');
      this.controls = this.querySelector('[data-hl-product-controls]');
      this.hint = this.querySelector('[data-hl-product-hint]');
      if (!this.track || !this.previous || !this.next) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      };
      this.onKey = (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        }
      };
      this.previous.addEventListener('click', this.onPrevious);
      this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, {passive: true});
      this.track.addEventListener('keydown', this.onKey);
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => this.update());
        this.resize.observe(this.track);
      } else {
        window.addEventListener('resize', this.onScroll);
      }
      this.update();
    }
    move(direction) {
      const first = this.track.querySelector('[data-hl-product-item]');
      if (!first) return;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap) || 0;
      const sign = style.direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({left: direction * sign * (first.getBoundingClientRect().width + gap), behavior: this.reduced.matches ? 'auto' : 'smooth'});
    }
    update() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.abs(this.track.scrollLeft);
      const overflow = maximum > 1;
      this.previous.disabled = !overflow || position <= 1;
      this.next.disabled = !overflow || position >= maximum - 1;
      if (this.controls) this.controls.hidden = !overflow;
      if (this.hint) this.hint.hidden = !overflow;
    }
    disconnectedCallback() {
      this.resize?.disconnect();
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('keydown', this.onKey);
      this.track = null;
      this.frame = null;
    }
  }
  customElements.define('healthy-story-products', HealthyStoryProducts);
})();
